import { useMemo, useState } from "react";
import { endOfMonth, format } from "date-fns";
import { useGetList, useTranslate } from "ra-core";
import { ArrowDown, ArrowUp, Minus, Wallet } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { AccountBalance, Payment } from "../../types";

type AccountRow = {
  id: string | number;
  name: string;
  currency: string;
  current: number;
  income: number;
  expense: number;
  projected: number;
};

const formatMoney = (value: number, currency: string) => {
  const sign = value < 0 ? "-" : "";
  return (
    sign +
    Math.abs(value).toLocaleString("ru-RU") +
    (currency === "RUB" ? " ₽" : ` ${currency}`)
  );
};

/**
 * Forecast wallet balances on a chosen date.
 *
 * Current balance comes from the `account_balances` view (sum of recorded
 * `transactions`, i.e. actuals incl. payments already marked as paid).
 * Then we add planned `payments` with status≠paid and planned_date ≤ chosen
 * date, attributed to their `account_id`. Payments without an account are
 * shown separately as "Без счёта" — they don't move a specific wallet's
 * projected balance but the user sees what's missing context.
 */
export const PlannedBalanceWidget = () => {
  const translate = useTranslate();
  const [dateIso, setDateIso] = useState<string>(() =>
    format(endOfMonth(new Date()), "yyyy-MM-dd"),
  );

  const { data: balances, isPending: balancesPending } =
    useGetList<AccountBalance>("account_balances", {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
      filter: { "archived_at@is": null },
    });

  // Planned payments up to the chosen date (status != paid).
  const { data: planned, isPending: plannedPending } = useGetList<Payment>(
    "payments",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "planned_date", order: "ASC" },
      filter: {
        "status@neq": "paid",
        "planned_date@lte": dateIso,
      },
    },
  );

  const { rows, unassigned } = useMemo(() => {
    const byAccount = new Map<number | string, { income: number; expense: number }>();
    let unassignedIncome = 0;
    let unassignedExpense = 0;
    for (const p of planned ?? []) {
      const amt = Number(p.amount) || 0;
      if (p.account_id == null) {
        if (p.direction === "income") unassignedIncome += amt;
        else unassignedExpense += amt;
        continue;
      }
      const cur = byAccount.get(p.account_id) ?? { income: 0, expense: 0 };
      if (p.direction === "income") cur.income += amt;
      else cur.expense += amt;
      byAccount.set(p.account_id, cur);
    }

    const rows: AccountRow[] = (balances ?? []).map((b) => {
      const planned = byAccount.get(b.account_id) ?? { income: 0, expense: 0 };
      return {
        id: b.account_id,
        name: b.name,
        currency: b.currency,
        current: Number(b.current_balance) || 0,
        income: planned.income,
        expense: planned.expense,
        projected:
          (Number(b.current_balance) || 0) + planned.income - planned.expense,
      };
    });

    return {
      rows,
      unassigned: {
        income: unassignedIncome,
        expense: unassignedExpense,
      },
    };
  }, [balances, planned]);

  const isPending = balancesPending || plannedPending;
  const hasUnassigned = unassigned.income > 0 || unassigned.expense > 0;

  return (
    <Card className="mb-3">
      <CardContent className="px-4 py-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Wallet className="h-4 w-4 text-muted-foreground" />
            {translate("finance.forecast.balanceTitle", {
              _: "Плановый баланс счетов",
            })}
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">
              {translate("finance.forecast.onDate", { _: "На дату" })}
            </label>
            <Input
              type="date"
              value={dateIso}
              min={format(new Date(), "yyyy-MM-dd")}
              onChange={(e) => {
                if (e.target.value) setDateIso(e.target.value);
              }}
              className="h-8 w-[10.5rem]"
            />
          </div>
        </div>

        {isPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("finance.forecast.noAccounts", {
              _: "Нет активных счетов",
            })}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {rows.map((row) => (
              <BalanceRow key={row.id} row={row} />
            ))}
            {hasUnassigned && (
              <UnassignedRow
                income={unassigned.income}
                expense={unassigned.expense}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BalanceRow = ({ row }: { row: AccountRow }) => {
  const delta = row.projected - row.current;
  const deltaPositive = delta > 0;
  const deltaNegative = delta < 0;

  return (
    <div className="rounded-md border bg-card p-3 flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate" title={row.name}>
          {row.name}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatMoney(row.current, row.currency)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "text-base font-semibold tabular-nums",
            row.projected < 0 ? "text-rose-600" : "",
          )}
        >
          {formatMoney(row.projected, row.currency)}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs tabular-nums",
            deltaPositive && "text-emerald-600",
            deltaNegative && "text-rose-600",
            !deltaPositive && !deltaNegative && "text-muted-foreground",
          )}
        >
          {deltaPositive ? (
            <ArrowUp className="h-3 w-3" />
          ) : deltaNegative ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <Minus className="h-3 w-3" />
          )}
          {formatMoney(Math.abs(delta), row.currency)}
        </span>
      </div>
      {(row.income || row.expense) ? (
        <div className="flex items-center gap-3 text-xs">
          {row.income > 0 && (
            <span className="text-emerald-600 tabular-nums">
              +{row.income.toLocaleString("ru-RU")} ₽
            </span>
          )}
          {row.expense > 0 && (
            <span className="text-rose-600 tabular-nums">
              −{row.expense.toLocaleString("ru-RU")} ₽
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
};

const UnassignedRow = ({
  income,
  expense,
}: {
  income: number;
  expense: number;
}) => {
  const translate = useTranslate();
  return (
    <div
      className="rounded-md border border-dashed bg-muted/30 p-3 flex flex-col gap-1"
      title={translate("finance.forecast.unassignedHint", {
        _: "У этих плановых записей не указан счёт — они не учтены в прогнозе по конкретному счёту.",
      })}
    >
      <span className="text-sm font-medium text-muted-foreground">
        {translate("finance.forecast.unassigned", { _: "Без счёта" })}
      </span>
      <div className="flex items-center gap-3 text-xs">
        {income > 0 && (
          <span className="text-emerald-600 tabular-nums">
            +{income.toLocaleString("ru-RU")} ₽
          </span>
        )}
        {expense > 0 && (
          <span className="text-rose-600 tabular-nums">
            −{expense.toLocaleString("ru-RU")} ₽
          </span>
        )}
      </div>
    </div>
  );
};
